import api, { BACKEND_URL } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";

const CHUNK = 512 * 1024; // 512KB chunks — friendly to weak networks
const DIRECT_THRESHOLD = 25 * 1024 * 1024; // ≥25MB → upload straight to IONOS (browser → bucket)

// Build a viewable media URL from whatever the API stored. Both the web app and
// the separate native app write into the same DB, so we accept several shapes:
//   • absolute http(s) / data: / blob:  → used as-is
//   • bare GridFS ObjectId ("6ab…")     → /api/files/{id}
//   • root-relative ("/api/files/…", "/media/…")
//   • bare relative ("files/…")         → made root-relative
// Media <img>/<video> can't send an Authorization header; the backend serves
// /api/files by (unguessable) id without a token, and we still append the JWT
// for backward-compat.
// Old native-app messages used type "voice"/"file"; render them as "audio"/"document".
export const msgType = (type) => (type === "voice" ? "audio" : type === "file" ? "document" : type);

export const fileUrl = (path) => {
  if (!path) return path;
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  // bare GridFS (24-hex) or IONOS (32-hex) id, optionally with a sub-path like "/thumb"
  if (/^([a-f0-9]{24}|[a-f0-9]{32})(\/|$)/i.test(path)) path = `/api/files/${path}`;
  else if (!path.startsWith("/")) path = `/${path}`;
  const token = localStorage.getItem("rc_token");
  const needsToken = path.startsWith("/api/files") && token;
  const sep = path.includes("?") ? "&" : "?";
  return `${BACKEND_URL}${path}${needsToken ? `${sep}token=${token}` : ""}`;
};

// Upload a File in resumable chunks. Large files (≥25MB) go straight to IONOS
// object storage via presigned multipart (faster, lighter, no proxy hop);
// smaller files use the proxied chunked path. Returns { url, content_type, name }.
export async function uploadFileChunked(file, onProgress, module = "chat") {
  if (file.size >= DIRECT_THRESHOLD) {
    try {
      return await uploadFileDirect(file, onProgress, module);
    } catch (e) {
      // 409 = IONOS not configured; otherwise fall back to the proxied path.
      if (e?.response?.status && e.response.status !== 409) {
        // real upload error — surface it rather than silently retrying the slow path
        if (e?.__ionos_fatal) throw e;
      }
      // fall through to proxied chunked upload
    }
  }
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK));
  const { data: init } = await api.post("/uploads/init", {
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    size: file.size,
    total_chunks: totalChunks,
    module,
  });
  const uploadId = init.upload_id;

  for (let i = 0; i < totalChunks; i++) {
    const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
    let attempt = 0;
    while (true) {
      try {
        await api.put(`/uploads/${uploadId}/chunk/${i}`, blob, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        break;
      } catch (e) {
        attempt += 1;
        if (attempt >= 4) throw e;
        await new Promise((r) => setTimeout(r, 800 * attempt)); // retry with backoff
      }
    }
    onProgress && onProgress(Math.round(((i + 1) / totalChunks) * 100));
  }
  const { data } = await api.post(`/uploads/${uploadId}/complete`);
  return { url: data.url, content_type: data.content_type, name: data.name };  // bare /api path; token added at render
}

// Presigned multipart upload: browser PUTs each part directly to IONOS.
export async function uploadFileDirect(file, onProgress, module = "chat") {
  const ctype = file.type || "application/octet-stream";
  const { data: init } = await api.post("/uploads/direct/init", {
    filename: file.name, content_type: ctype, size: file.size, module,
  });
  const { file_id, key, upload_id, part_size, parts } = init;
  const done = new Array(parts.length).fill(0);
  const etags = [];
  try {
    for (const p of parts) {
      const start = (p.part_number - 1) * part_size;
      const blob = file.slice(start, Math.min(start + part_size, file.size));
      let attempt = 0;
      while (true) {
        try {
          const res = await fetch(p.url, {
            method: "PUT", body: blob,
            headers: { "Content-Type": ctype },
          });
          if (!res.ok) throw new Error(`part ${p.part_number} HTTP ${res.status}`);
          const etag = res.headers.get("ETag") || res.headers.get("etag");
          if (!etag) throw new Error(`part ${p.part_number} missing ETag (check bucket CORS ExposeHeaders)`);
          etags.push({ PartNumber: p.part_number, ETag: etag });
          break;
        } catch (err) {
          attempt += 1;
          if (attempt >= 4) { err.__ionos_fatal = true; throw err; }
          await new Promise((r) => setTimeout(r, 800 * attempt));
        }
      }
      done[p.part_number - 1] = blob.size;
      if (onProgress) {
        const uploaded = done.reduce((a, b) => a + b, 0);
        onProgress(Math.round((uploaded / file.size) * 100));
      }
    }
    const { data } = await api.post("/uploads/direct/complete", {
      file_id, key, upload_id, parts: etags,
      filename: file.name, content_type: ctype, size: file.size, module,
    });
    return { url: data.url, content_type: data.content_type, name: data.name };
  } catch (e) {
    api.post("/uploads/direct/abort", { key, upload_id }).catch(() => {});
    throw e;
  }
}

// Profile photo: resize to max 512x512 JPEG, upload, and return the "/api/files/<id>" link (never a data URL).
export async function uploadAvatar(file) {
  const small = await compressImage(file, { maxDim: 512, quality: 0.85 });
  const { url } = await uploadFileChunked(small, null, "chat");
  return url;
}
